import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import GlobalStyles from './styles/GlobalStyles';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ResourceList from './components/common/ResourceList';
import CustomersList from './pages/Customers/CustomersList';
import CustomerDetails from './pages/Customers/CustomerDetails';
import CustomerFunnelsList from './pages/CustomerFunnels/CustomerFunnelsList';
import CustomerFunnelForm from './pages/CustomerFunnels/CustomerFunnelForm';
import NotificationsList from './pages/Notifications/NotificationsList';
import NotificationForm from './pages/Notifications/NotificationForm';
import TestRunDetails from './pages/TestRuns/TestRunDetails';
import TestRunsList from './pages/TestRuns/TestRunsList';
import AppVersionsList from './pages/AppVersions/AppVersionsList';
import PromptTemplateForm from './pages/PromptTemplates/PromptTemplateForm';
import PromptManagement from './pages/PromptManagement/PromptManagement';
import JobsPage from './pages/Jobs/JobsPage';
import { api } from './services/api';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Wrapper to provide theme to styled-components
const ThemedApp = ({ children }) => {
  const { isDark } = useTheme();
  return (
    <StyledThemeProvider theme={{ isDark }}>
      <GlobalStyles />
      {children}
    </StyledThemeProvider>
  );
};

// Resource Pages
const UsersList = () => (
  <ResourceList
    title="Users"
    resource="users"
    apiMethod={api.getUsers}
    columns={[
      { key: 'email', label: 'Email' },
      { key: 'name', label: 'Name' },
      { key: 'role', label: 'Role' },
      { key: 'createdAt', label: 'Created At', render: (val) => new Date(val).toLocaleDateString() },
    ]}
    onEdit={(id) => {}}
    onDelete={api.deleteUser}
    onCreate={true}
    searchFields={['email', 'name']}
  />
);

const SubscriptionsList = () => (
  <ResourceList
    title="Subscriptions"
    resource="subscriptions"
    apiMethod={api.getSubscriptions}
    columns={[
      { key: 'customer', label: 'Customer', render: (val) => val?.email || val?.id },
      { key: 'plan', label: 'Plan' },
      { key: 'status', label: 'Status' },
      { key: 'createdAt', label: 'Created At', render: (val) => new Date(val).toLocaleDateString() },
    ]}
    onEdit={(id) => {}}
    onDelete={api.deleteSubscription}
    onCreate={true}
  />
);


const AchievementsList = () => (
  <ResourceList
    title="Achievements"
    resource="achievements"
    apiMethod={api.getAchievements}
    columns={[
      { key: 'name', label: 'Name' },
      { key: 'description', label: 'Description', render: (val) => val?.substring(0, 50) + '...' },
      { key: 'points', label: 'Points' },
      { key: 'createdAt', label: 'Created At', render: (val) => new Date(val).toLocaleDateString() },
    ]}
    onEdit={(id) => {}}
    onDelete={api.deleteAchievement}
    onCreate={true}
  />
);

// AppVersionsList is now imported from pages/AppVersions/AppVersionsList

const WhatsNewList = () => (
  <ResourceList
    title="What's New"
    resource="whats-new"
    apiMethod={api.getWhatsNew}
    columns={[
      { key: 'title', label: 'Title' },
      { key: 'description', label: 'Description', render: (val) => val?.substring(0, 50) + '...' },
      { key: 'version', label: 'Version' },
      { key: 'createdAt', label: 'Created At', render: (val) => new Date(val).toLocaleDateString() },
    ]}
    onEdit={(id) => {}}
    onDelete={api.deleteWhatsNew}
    onCreate={true}
  />
);

const FeaturesList = () => (
  <ResourceList
    title="Features"
    resource="features"
    apiMethod={api.getFeatures}
    columns={[
      { key: 'name', label: 'Name' },
      { key: 'description', label: 'Description', render: (val) => val?.substring(0, 50) + '...' },
      { key: 'isActive', label: 'Active', render: (val) => val ? 'Yes' : 'No' },
      { key: 'createdAt', label: 'Created At', render: (val) => new Date(val).toLocaleDateString() },
    ]}
    onEdit={(id) => {}}
    onDelete={api.deleteFeature}
    onCreate={true}
  />
);

const HelpCenter = () => (
  <ResourceList
    title="Help Center"
    resource="help-center"
    apiMethod={api.getConversations}
    columns={[
      { key: 'customer', label: 'Customer', render: (val) => val?.email || val?.id },
      { key: 'status', label: 'Status' },
      { key: 'createdAt', label: 'Created At', render: (val) => new Date(val).toLocaleDateString() },
    ]}
  />
);

const AdminApp = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <ThemedApp>
          <BrowserRouter>
            <Routes>
              <Route path="/admin/login" element={<Login />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/customers"
                element={
                  <ProtectedRoute>
                    <CustomersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/customers/:id"
                element={
                  <ProtectedRoute>
                    <CustomerDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute>
                    <UsersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/subscriptions"
                element={
                  <ProtectedRoute>
                    <SubscriptionsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/achievements"
                element={
                  <ProtectedRoute>
                    <AchievementsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/app-versions"
                element={
                  <ProtectedRoute>
                    <AppVersionsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/whats-new"
                element={
                  <ProtectedRoute>
                    <WhatsNewList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/features"
                element={
                  <ProtectedRoute>
                    <FeaturesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/jobs"
                element={
                  <ProtectedRoute>
                    <JobsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/test-runs"
                element={
                  <ProtectedRoute>
                    <TestRunsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/test-runs/:id"
                element={
                  <ProtectedRoute>
                    <TestRunDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/help-center"
                element={
                  <ProtectedRoute>
                    <HelpCenter />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/customerFunnels"
                element={
                  <ProtectedRoute>
                    <CustomerFunnelsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/customerFunnels/new"
                element={
                  <ProtectedRoute>
                    <CustomerFunnelForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/customerFunnels/:id"
                element={
                  <ProtectedRoute>
                    <CustomerFunnelForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications/new"
                element={
                  <ProtectedRoute>
                    <NotificationForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications/:id"
                element={
                  <ProtectedRoute>
                    <NotificationForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/prompt-management"
                element={
                  <ProtectedRoute>
                    <PromptManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/prompt-templates/new"
                element={
                  <ProtectedRoute>
                    <PromptTemplateForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/prompt-templates/:id"
                element={
                  <ProtectedRoute>
                    <PromptTemplateForm />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </BrowserRouter>
        </ThemedApp>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default AdminApp;

